import type { InteractiveView } from '../../../src/shared/interactiveView';
import { parseViewBridgeMessage } from '../../lib/interactiveViewProtocol';
import { randomId } from 'elowen-plugin-shared/randomId';
import { htmlFrameCsp } from '../../lib/htmlFrameCsp';

const encode = (value: unknown) => JSON.stringify(value).replaceAll('<', '\\u003c');

/** A trusted outer shell blocks navigation of the untrusted inner frame with frame-src.
 * A single sandboxed srcdoc cannot block its own location changes in current browsers.
 *
 * `prose` is the host's resolved conversation role (size, line height, weight as declarations), so plain text
 * in a view reads at the size of the reply around it. The face stays `system-ui`: the frame's CSP has
 * `font-src 'none'`, so the host's web font cannot load there and the platform sans is what it would fall to. */
export function interactiveViewDocument(view: InteractiveView, tokens: Record<string, string>, buttons: Record<string, string>, prose: string, locked: boolean, clickable: boolean, title: string): string {
  const nonce = randomId();
  const innerBoot = `
    const payload = ${encode({ view, tokens, buttons, prose, locked, clickable })};
    const root = document.documentElement;
    for (const [name, value] of Object.entries(payload.tokens)) root.style.setProperty(name, value);
    const style = document.createElement('style');
    // The host sizes this frame to the reported height, so the document never scrolls vertically. At a
    // fractional device pixel ratio the frame box snaps a fraction below that height and would paint a scrollbar.
    style.textContent = '*{box-sizing:border-box}html{overflow-y:hidden}body{display:flow-root;margin:0;background:transparent;color:var(--color-foreground);font-family:system-ui;'+payload.prose+'overflow-wrap:anywhere}button{'+payload.buttons.accent+'}button[data-variant="outline"]{'+payload.buttons.outline+'}button[data-variant="default"]{'+payload.buttons.default+'}button:disabled{opacity:.4;cursor:not-allowed}button:focus-visible{outline:2px solid var(--color-primary);outline-offset:2px}'+(payload.view.css || '');
    document.head.append(style);
    document.body.innerHTML = payload.view.html;
    document.body.inert = payload.locked;
    if (payload.locked) for (const button of document.querySelectorAll('button')) button.disabled = true;
    window.elowen = Object.freeze({action(value) {
      if (payload.clickable && !payload.locked) parent.postMessage({type:'elowen-view-action',value}, '*');
    }});
    const resize = () => parent.postMessage({type:'elowen-view-height',height:Math.min(10000,Math.max(1,document.body.scrollHeight))}, '*');
    new ResizeObserver(resize).observe(document.body);
    const script = document.createElement('script');
    script.nonce = ${encode(nonce)};
    script.textContent = payload.view.js || '';
    document.body.append(script);
    resize();
  `;
  const inner = `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${htmlFrameCsp({ nonce })}"></head><body><script nonce="${nonce}">${innerBoot}</script></body></html>`;
  const outerBoot = `
    document.documentElement.style.colorScheme = ${encode(tokens['color-scheme'])};
    addEventListener('DOMContentLoaded', () => {
    const parse = ${parseViewBridgeMessage.toString()};
    const child = document.createElement('iframe');
    child.title = ${encode(title)};
    child.sandbox = 'allow-scripts';
    child.referrerPolicy = 'no-referrer';
    child.srcdoc = ${encode(inner)};
    document.body.append(child);
    addEventListener('message', event => {
      if (event.source !== child.contentWindow) return;
      const value = parse(event.data);
      if (!value) return;
      if (value.type === 'elowen-view-height') child.style.height = value.height + 'px';
      parent.postMessage(value, '*');
    });
    }, { once: true });
  `;
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${htmlFrameCsp({ nonce, frames: true })}"><style>html,body{margin:0;background:transparent}iframe{border:0;display:block;width:100%;height:180px}</style></head><body><script nonce="${nonce}">${outerBoot}</script></body></html>`;
}
